/**
 * scheduleQualification.ts
 * Pure helpers that decide whether a candidate employee is "qualified" to be
 * assigned to a ScheduleTask of a given task type, and whether a loaded
 * candidate list contains anyone qualified.
 *
 * BUG-2026-0094 — "Agent dropdown list should not display unqualified users."
 *   A user is qualified for a task type iff they have an ACTIVE SkillMatrix row
 *   for that task type's REQUIRED SKILL (Lab Taxonomy 2026: tests map N:1 onto
 *   skills via TaskType.SkillKey), i.e. `listAssignableUsers(taskTypeKey)`
 *   returned a non-null `SkillLevel` for them. A null SkillLevel means no
 *   recorded level for the required skill.
 *
 *   Lab Taxonomy 2026: some tests require NO skill at all (TaskType.SkillKey is
 *   NULL — e.g. fee lines, gradation building). The backend flags these rows
 *   with `SkillRequired: false`; qualification then cannot be judged by skill,
 *   so EVERYONE qualifies (matching the mentor gate, which always passes for
 *   no-skill tests).
 *
 *   Skill levels are constrained to 1–5 on every write path (see
 *   skillMatrixService.ts), so level 0 can never be persisted — but we treat any
 *   `<= 0` as un-qualified defensively, so a stray 0 (e.g. a future data import)
 *   can never sneak an un-rated person back into the picker.
 *
 *   When the task has NO task type, qualification can't be judged (SkillLevel is
 *   not even requested from the backend), so we fall back to "everyone qualifies"
 *   — the pre-BUG-2026-0094 behaviour.
 *
 * This module is the SINGLE source of truth for the qualification gate, shared
 * by InlineAssigneePicker (the quick inline dropdown) and AssignEmployeesDialog
 * (the advanced "Assign Employees" dialog) so the two surfaces can never drift
 * apart again. The first fix (PR #96) only patched the inline picker; the dialog
 * kept leaking un-rated users because it had its own un-shared filter.
 *
 * Intentionally dependency-free so it can be unit-tested directly with tsx.
 */

/** Minimal shape needed to judge qualification — a subset of AssignableUser. */
export interface QualifiableUser {
  /**
   * SkillMatrix level (1–5) for the task type's required skill.
   *   - number  → has a recorded level for the required skill (qualified)
   *   - null    → no recorded level (NOT qualified, unless no skill is required)
   *   - undefined → list was loaded WITHOUT a task type (skill not requested)
   */
  SkillLevel?: number | null;
  /**
   * Whether the task type requires a skill at all (Lab Taxonomy 2026).
   *   - false → TaskType.SkillKey is NULL: no gate, everyone qualifies
   *   - true / undefined → skill-gated (undefined = older payloads, treat as gated)
   */
  SkillRequired?: boolean;
}

/**
 * True when `user` is qualified to be assigned to a task of `taskTypeKey`.
 *
 * @param user        Candidate carrying a (possibly absent) SkillLevel.
 * @param taskTypeKey The task's TaskTypeKey, or null when the task has no type.
 */
export function isQualifiedForTaskType(
  user: QualifiableUser,
  taskTypeKey: string | null | undefined,
): boolean {
  // No task type → qualification can't be judged → everyone qualifies.
  if (!taskTypeKey) return true;
  // Task type requires no skill (Lab Taxonomy 2026) → everyone qualifies.
  if (user.SkillRequired === false) return true;
  // Skill-gated → require a recorded, positive level for the required skill.
  return user.SkillLevel != null && user.SkillLevel > 0;
}

/**
 * True when the loaded candidate list contains at least one qualified user for
 * `taskTypeKey`. Drives a clearer "No qualified users for this task type"
 * empty-state message when a task type is set but nobody is rated.
 *
 * Always true when there is no task type (we then show everyone).
 *
 * @param users       The full list returned by listAssignableUsers.
 * @param taskTypeKey The task's TaskTypeKey, or null when the task has no type.
 */
export function listHasQualifiedCandidate(
  users: QualifiableUser[],
  taskTypeKey: string | null | undefined,
): boolean {
  if (!taskTypeKey) return true;
  return users.some((u) => isQualifiedForTaskType(u, taskTypeKey));
}
